// Возврат фокуса после закрытия слоя на элемент, который его открыл (README «Доступность»).
// Radix возвращает фокус только на свой Trigger; окна сервиса открываются и кодом (из меню,
// из строки таблицы), поэтому опору запоминаем сами в момент открытия. Если, пока слой
// закрывался, страница сама перевела фокус (переход из ленты уведомлений на строку заказа),
// его не перебиваем: возврат — только когда фокус ушёл вместе со слоем.
//
// 044 (MOT-06, research Р5): слой уходит с выходом, и Radix зовёт onCloseAutoFocus только после
// него — фокус вернулся бы через 120–240 мс. Фокус возвращается в начале выхода: в эффекте
// после отрисовки закрытия, когда Radix уже снял удержание фокуса в окне. onCloseAutoFocus
// второй раз фокус не ставит.
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';

const OTHER_LAYERS =
  '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]';

export interface ReturnFocusOptions {
  /** Явное место возврата: кнопка «Создать ▾», если окно открыли из её меню. */
  returnFocusRef?: RefObject<HTMLElement | null>;
  /** Пока слой открыт, фокус снаружи меняет место возврата (боковая панель без затемнения). */
  trackWhileOpen?: boolean;
}

/**
 * Фокус уже на странице, а не в закрытом слое и не потерян (на body): его перевёл код страницы,
 * и возврат на кнопку слоя его бы перебил.
 */
export function focusMovedOn(content?: HTMLElement | null): boolean {
  const active = document.activeElement;
  return active instanceof HTMLElement && active !== document.body && !content?.contains(active);
}

export function useReturnFocus(
  open: boolean,
  contentRef: RefObject<HTMLElement | null>,
  { returnFocusRef, trackWhileOpen = false }: ReturnFocusOptions = {},
): (event: Event) => void {
  const openerRef = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const remember = (element: EventTarget | null) => {
      if (
        element instanceof HTMLElement &&
        element !== document.body &&
        !contentRef.current?.contains(element)
      ) {
        openerRef.current = element;
      }
    };
    remember(document.activeElement);
    if (!trackWhileOpen) return undefined;
    // Фокус в других слоях (окно подтверждения, меню, подсказка) место возврата не меняет.
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(OTHER_LAYERS)) return;
      remember(target);
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, [open, trackWhileOpen, contentRef]);

  const back = useCallback(() => {
    if (focusMovedOn(contentRef.current)) return;
    const target = returnFocusRef?.current ?? openerRef.current;
    if (target?.isConnected) target.focus();
  }, [returnFocusRef, contentRef]);

  // Фокус вернули в начале выхода: при снятии слоя (onCloseAutoFocus) — уже не нужно.
  const settledRef = useExitStart(open, back);

  return useCallback(
    (event: Event) => {
      event.preventDefault();
      if (settledRef.current) {
        settledRef.current = false;
        return;
      }
      back();
    },
    [back, settledRef],
  );
}

/**
 * Начало выхода: open сменился с true на false. onExit — в эффекте после отрисовки закрытия.
 * Возвращает метку «выход начался, onExit уже был»; её снимает тот, кто обработал снятие слоя,
 * и новое открытие.
 */
function useExitStart(open: boolean, onExit: () => void): RefObject<boolean> {
  const wasOpen = useRef(open);
  const settledRef = useRef(false);
  const exitRef = useRef(onExit);
  useLayoutEffect(() => {
    exitRef.current = onExit;
  });
  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      settledRef.current = false;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    settledRef.current = true;
    exitRef.current();
  }, [open]);
  return settledRef;
}

export interface ExitFocusOptions {
  /** Кнопка слоя: на неё возвращается фокус. */
  triggerRef: RefObject<HTMLElement | null>;
  /** Содержимое слоя: фокус в нём (или потерянный) возвращается на кнопку. */
  contentRef: RefObject<HTMLElement | null>;
  /**
   * Обработчик вызывающего: событие приходит в начале выхода; preventDefault — фокус ставит
   * он сам (меню у строки таблицы возвращает фокус в таблицу).
   */
  onCloseAutoFocus?: (event: Event) => void;
}

export interface ExitFocusHandlers {
  onInteractOutside: (event: Event) => void;
  onCloseAutoFocus: (event: Event) => void;
}

/**
 * Меню и всплывающая панель на Radix: фокус на кнопку — в начале выхода, а не после него
 * (044, MOT-06). Как у Radix: после нажатия снаружи фокус на кнопку не возвращается. Если слой
 * сняли целиком, без закрытия (вызывающий убрал его со страницы), — прежний путь Radix.
 */
export function useExitFocus(
  open: boolean,
  { triggerRef, contentRef, onCloseAutoFocus }: ExitFocusOptions,
): ExitFocusHandlers {
  const outside = useRef(false);
  const handlerRef = useRef(onCloseAutoFocus);
  useLayoutEffect(() => {
    handlerRef.current = onCloseAutoFocus;
  });
  useLayoutEffect(() => {
    if (open) outside.current = false;
  }, [open]);

  const exit = useCallback(() => {
    // Фокус уходит из слоя вместе с его закрытием, как при снятии: обработчик вызывающего видит
    // то же, что видел после выхода («фокус потерян — вернуть»).
    const active = document.activeElement;
    if (active instanceof HTMLElement && contentRef.current?.contains(active)) active.blur();
    const event = new Event('closeautofocus', { cancelable: true });
    handlerRef.current?.(event);
    if (event.defaultPrevented || outside.current || focusMovedOn(contentRef.current)) return;
    const trigger = triggerRef.current;
    if (trigger?.isConnected) trigger.focus();
  }, [contentRef, triggerRef]);
  const settledRef = useExitStart(open, exit);

  return {
    onInteractOutside: useCallback(() => {
      outside.current = true;
    }, []),
    onCloseAutoFocus: useCallback(
      (event: Event) => {
        if (settledRef.current) {
          settledRef.current = false;
          event.preventDefault();
          return;
        }
        handlerRef.current?.(event);
      },
      [settledRef],
    ),
  };
}
